import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, Grid, MenuItem, Typography } from '@mui/material';
import { Info } from '@phosphor-icons/react';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { FlowService } from '../../../service/FlowService';
import { useNotificationStore } from '../../../store/storeNotifications';
import storeThemeChat from '../../../store/storeThemeChat';
import { MuiSelectControlled } from '../../Form/Select';
import { MuiSwitchControlled } from '../../Form/Switch';
import { MuiUploadtControlled } from '../../Form/Upload';
import { PreviewChatbot } from './PreviewChatbot';

const CustomizeInterfaceSchema = z.object({
  favicon: z.nullable(z.string()),
  logo: z.nullable(z.string()),
  chatImage: z.nullable(z.string()),
  backgroundImage: z.nullable(z.string()),
  iconShape: z.nullable(z.string()),
  iconSize: z.nullable(z.any()),
  autoOpenChatbotOnLoad: z.boolean(),
  disableFluxpromptBranding: z.boolean(),
});

type ICustomizeInterface = z.infer<typeof CustomizeInterfaceSchema>;

export function LogoAndIcons({ chatData, onSetFlow }: any) {
  const setNotification = useNotificationStore(state => state.setNotification);
  const theme = storeThemeChat();
  const formsMethods = useForm<ICustomizeInterface>({
    resolver: zodResolver(CustomizeInterfaceSchema),
    defaultValues: {
      favicon: chatData.favicon,
      logo: chatData.logo,
      chatImage: chatData.chatImage,
      backgroundImage: chatData.backgroundImage,
      iconShape: chatData.iconShape === null ? 'square' : chatData.iconShape,
      iconSize: chatData.iconSize === null ? 16 : Number(chatData.iconSize),
      autoOpenChatbotOnLoad:
        chatData.autoOpenChatbotOnLoad === null
          ? false
          : chatData.autoOpenChatbotOnLoad,
      disableFluxpromptBranding:
        chatData.disableFluxpromptBranding === null
          ? false
          : chatData.disableFluxpromptBranding,
    },
  });

  async function handleSubmitForm(data: ICustomizeInterface) {
    try {
      const response = await FlowService.updateFlow(chatData.id, {
        favicon: data.favicon,
        logo: data.logo,
        chatImage: data.chatImage,
        backgroundImage: data.backgroundImage,
        iconShape: data.iconShape,
        iconSize: data.iconSize,
        autoOpenChatbotOnLoad: data.autoOpenChatbotOnLoad,
        disableFluxpromptBranding: data.disableFluxpromptBranding,
      });

      onSetFlow(response.data);

      setNotification({
        type: 'success',
        message: 'Saved successfully',
      });
    } catch (error) {
      setNotification({
        type: 'error',
        message: 'Error saving customize interface form',
      });
    }
  }

  async function handleDeleteImage(type: any) {
    try {
      const response = await FlowService.updateFlow(chatData.id, {
        [type]: null,
      });

      onSetFlow(response.data);
      formsMethods.setValue(type, null);

      setNotification({
        type: 'success',
        message: 'Saved successfully',
      });
    } catch (error) {
      setNotification({
        type: 'error',
        message: 'Error saving customize interface form',
      });
    }
  }

  function definesThemeProperties(defaultTheme: any) {
    switch (defaultTheme) {
      case 'black-light':
        theme.setDefaultTheme('black-light');
        theme.setBrandColor('#101828');
        theme.setInputBorderColor('#EAECF0');
        theme.setFont('Roboto');
        theme.setFontWeight('400');
        theme.setPageBackground('#FFF');
        theme.setPageText('#475467');
        theme.setResponseContainer('#F2F4F7');
        theme.setResponseText('#101828');
        theme.setUserContainer('#F2F4F7');
        theme.setUserText('#475467');

        break;

      case 'blue-light':
        theme.setDefaultTheme('blue-light');
        theme.setBrandColor('#3650DE');
        theme.setInputBorderColor('#EAECF0');
        theme.setFont('Roboto');
        theme.setFontWeight('400');
        theme.setPageBackground('#FFF');
        theme.setPageText('#475467');
        theme.setResponseContainer('#F2F4F7');
        theme.setResponseText('#101828');
        theme.setUserContainer('#F2F4F7');
        theme.setUserText('#475467');

        break;

      case 'purple-light':
        console.log('caiu aqui');
        theme.setDefaultTheme('purple-light');
        theme.setBrandColor('#7B4CFE');
        theme.setInputBorderColor('#EAECF0');
        theme.setFont('Roboto');
        theme.setFontWeight('400');
        theme.setPageBackground('#FFF');
        theme.setPageText('#475467');
        theme.setResponseContainer('#F2F4F7');
        theme.setResponseText('#101828');
        theme.setUserContainer('#F2F4F7');
        theme.setUserText('#475467');
        break;

      case 'orange-light':
        theme.setDefaultTheme('orange-light');
        theme.setBrandColor('#FF9103');
        theme.setInputBorderColor('#EAECF0');
        theme.setFont('Roboto');
        theme.setFontWeight('400');
        theme.setPageBackground('#FFF');
        theme.setPageText('#475467');
        theme.setResponseContainer('#F2F4F7');
        theme.setResponseText('#101828');
        theme.setUserContainer('#F2F4F7');
        theme.setUserText('#475467');
        break;

      case 'blue-dark':
        theme.setDefaultTheme('blue-dark');
        theme.setBrandColor('#3650DE');
        theme.setInputBorderColor('#344054');
        theme.setFont('Roboto');
        theme.setFontWeight('400');
        theme.setPageBackground('#1D2939');
        theme.setPageText('#D0D5DD');
        theme.setResponseContainer('#344054');
        theme.setResponseText('#FFFFFF');
        theme.setUserContainer('#101828');
        theme.setUserText('#D0D5DD');
        break;

      case 'orange-dark':
        theme.setDefaultTheme('orange-dark');
        theme.setBrandColor('#FF9103');
        theme.setInputBorderColor('#344054');
        theme.setFont('Roboto');
        theme.setFontWeight('400');
        theme.setPageBackground('#1D2939');
        theme.setPageText('#D0D5DD');
        theme.setResponseContainer('#344054');
        theme.setResponseText('#FFFFFF');
        theme.setUserContainer('#101828');
        theme.setUserText('#D0D5DD');
        break;

      case 'green-dark':
        theme.setDefaultTheme('green-dark');
        theme.setBrandColor('#4CAF50');
        theme.setInputBorderColor('#344054');
        theme.setFont('Roboto');
        theme.setFontWeight('400');
        theme.setPageBackground('#1D2939');
        theme.setPageText('#D0D5DD');
        theme.setResponseContainer('#344054');
        theme.setResponseText('#FFFFFF');
        theme.setUserContainer('#101828');
        theme.setUserText('#D0D5DD');
        break;

      case 'my-theme':
        theme.setDefaultTheme('my-theme');
        theme.setBrandColor(chatData.brandColor);
        theme.setInputBorderColor(chatData.brandColor);
        theme.setFont(chatData.font);
        theme.setFontWeight(chatData.fontWeight);
        theme.setPageBackground(chatData.pageBackground);
        theme.setPageText(chatData.pageText);
        theme.setResponseContainer(chatData.responseContainer);
        theme.setResponseText(chatData.responseText);
        theme.setUserContainer(chatData.userContainer);
        theme.setUserText(chatData.userText);
        break;
      default:
        theme.setDefaultTheme('blue-light');
        theme.setBrandColor('#3650DE');
        theme.setInputBorderColor('#EAECF0');
        theme.setFont('Roboto');
        theme.setFontWeight('regular');
        theme.setBackgroundImage(chatData.backgroundImage);
        theme.setPageBackground('#FFF');
        theme.setPageText('#475467');
        theme.setResponseContainer('#F2F4F7');
        theme.setResponseText('#101828');
        theme.setUserContainer('#F2F4F7');
        theme.setUserText('#475467');
    }
  }

  useEffect(() => {
    definesThemeProperties(chatData.defaultTheme);
  }, [chatData.defaultTheme]);

  const favicon = formsMethods.watch('favicon');
  const logo = formsMethods.watch('logo');
  const chatImage = formsMethods.watch('chatImage');
  const backgroundImage = formsMethods.watch('backgroundImage');
  const iconShape = formsMethods.watch('iconShape');
  const iconSize = formsMethods.watch('iconSize');

  useEffect(() => {
    theme.setFavicon(favicon);
    theme.setLogo(logo);
    theme.setChatImage(chatImage);
    theme.setBackgroundImage(backgroundImage);
    theme.setIconShape(iconShape);
    theme.setIconSize(iconSize);
  }, [favicon, iconShape, iconSize, logo, chatImage, backgroundImage]);

  return (
    <FormProvider {...formsMethods}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <form
            id="customize-interface"
            onSubmit={formsMethods.handleSubmit(handleSubmitForm)}
            style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
          >
            <Box display="flex" gap="8px">
              <Box display="flex" flexDirection="column" gap="4px">
                <Box display="flex" alignItems="center" gap="4px">
                  <Typography>Favicon</Typography>
                  <Info size={20} />

                  {favicon !== null && (
                    <Typography
                      onClick={() => handleDeleteImage('favicon')}
                      sx={{
                        color: 'red',
                        fontWeight: 400,
                        fontSize: '10px',
                        cursor: 'pointer',
                      }}
                    >
                      Delete File
                    </Typography>
                  )}
                </Box>

                <MuiUploadtControlled
                  uploadProps={{
                    name: 'favicon',
                    label: 'Upload Image',
                    accepts: '.png, .jpg, .jpeg',
                  }}
                  styles={{ width: '200px' }}
                />
              </Box>

              <Box display="flex" flexDirection="column" gap="4px">
                <Box display="flex" alignItems="center" gap="4px">
                  <Typography>Chat logo</Typography>
                  <Info size={20} />

                  {logo !== null && (
                    <Typography
                      onClick={() => handleDeleteImage('logo')}
                      sx={{
                        color: 'red',
                        fontWeight: 400,
                        fontSize: '10px',
                        cursor: 'pointer',
                      }}
                    >
                      Delete File
                    </Typography>
                  )}
                </Box>

                <MuiUploadtControlled
                  uploadProps={{
                    name: 'logo',
                    label: 'Upload Image',
                    accepts: '.png, .jpg, .jpeg',
                  }}
                  styles={{ width: '200px' }}
                />
              </Box>
            </Box>

            <Box display="flex" gap="8px">
              <Box display="flex" flexDirection="column" gap="4px">
                <Box display="flex" alignItems="center" gap="4px">
                  <Typography>Chat icon</Typography>
                  <Info size={20} />

                  {chatImage !== null && (
                    <Typography
                      onClick={() => handleDeleteImage('chatImage')}
                      sx={{
                        color: 'red',
                        fontWeight: 400,
                        fontSize: '10px',
                        cursor: 'pointer',
                      }}
                    >
                      Delete File
                    </Typography>
                  )}
                </Box>

                <MuiUploadtControlled
                  uploadProps={{
                    name: 'chatImage',
                    label: 'Upload Image',
                    accepts: '.png, .jpg, .jpeg',
                  }}
                  styles={{ width: '200px' }}
                />
              </Box>

              <Box display="flex" flexDirection="column" gap="4px">
                <Box display="flex" alignItems="center" gap="4px">
                  <Typography>Background Image</Typography>
                  <Info size={20} />

                  {backgroundImage !== null && (
                    <Typography
                      onClick={() => handleDeleteImage('backgroundImage')}
                      sx={{
                        color: 'red',
                        fontWeight: 400,
                        fontSize: '10px',
                        cursor: 'pointer',
                      }}
                    >
                      Delete File
                    </Typography>
                  )}
                </Box>

                <MuiUploadtControlled
                  uploadProps={{
                    name: 'backgroundImage',
                    label: 'Upload background Image',
                    // icon: Image,
                    accepts: '.png, .jpg, .jpeg',
                  }}
                  styles={{ width: '300px' }}
                />
              </Box>
            </Box>

            <Box display="flex" width="100%" gap="4px" marginTop="4px">
              <MuiSelectControlled
                selectProps={{
                  name: 'iconShape',
                  label: 'Embeded Icon Shape',
                  placeholder: 'Embeded Icon Shape',
                }}
              >
                <MenuItem value="square">Square</MenuItem>
                <MenuItem value="circle">Circle</MenuItem>
              </MuiSelectControlled>

              <MuiSelectControlled
                selectProps={{
                  name: 'iconSize',
                  label: 'Embeded Icon Size',
                  placeholder: 'Embeded Icon Size',
                }}
              >
                <MenuItem value={8}>8px</MenuItem>
                <MenuItem value={12}>12px</MenuItem>
                <MenuItem value={16}>16px</MenuItem>
                <MenuItem value={32}>32px</MenuItem>
              </MuiSelectControlled>
            </Box>

            <Box display="flex" flexDirection="column">
              <MuiSwitchControlled
                switchProps={{
                  name: 'autoOpenChatbotOnLoad',
                  label: `Automatically open Chatbot pop-up when page loads
                      (non-mobile only)`,
                }}
              />

              <MuiSwitchControlled
                switchProps={{
                  name: 'disableFluxpromptBranding',
                  label: ` Turn OFF Fluxprompt branding (Professional Subscription
                      Only)`,
                }}
              />
            </Box>
          </form>
        </Grid>

        <Grid item xs={12} md={4}>
          <PreviewChatbot />
        </Grid>
      </Grid>

      <Box display="flex" justifyContent="end" width="100%">
        <Button
          type="submit"
          form="customize-interface"
          variant="contained"
          sx={{ width: '160px', marginTop: '8px' }}
        >
          Save changes
        </Button>
      </Box>
    </FormProvider>
  );
}
